import ChainLogo from '@/components/icons/ChainLogo'
import CopyValue from '@/components/ui/copy-value'
import TokenLogo from '@/components/token-logo'
import StackTokenLogo from '@/components/token-logo/stack-token-logo'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { shortenAddress } from '@/utils'
import { Trans } from '@lingui/react/macro'
import {
  EUSD_ADDRESS,
  RGUSD_ADDRESS,
  RSR_ADDRESS,
  USD3_ADDRESS,
} from '@/utils/addresses'
import { ChainId } from '@/utils/chains'
import {
  ArrowUpRight,
  ArrowUpRightIcon,
  ChevronsUpDown,
  Info,
  OctagonAlert,
} from 'lucide-react'
import bridgeGGLogo from './assets/bridgegg.png'
import SuperbridgeLogo from './assets/superbridge.avif'
import portalLogo from './assets/portal.avif'

type Bridge = {
  name: string
  logo: string
  url: string
  listedTokens: string[]
  targetChain?: number
}

type BridgeTokenList = {
  [key: string]: { symbol: string; logo: string; l1: string; l2: string }
}

const PORTAL_TOKEN_LIST: BridgeTokenList = {
  rsr: {
    symbol: 'RSR',
    logo: '/svgs/rsr.svg',
    l1: RSR_ADDRESS[ChainId.Mainnet],
    l2: RSR_ADDRESS[ChainId.BSC],
  },
}

const SUPERBRIDGE_TOKEN_LIST: BridgeTokenList = {
  rsr: {
    symbol: 'RSR',
    logo: '/svgs/rsr.svg',
    l1: RSR_ADDRESS[ChainId.Mainnet],
    l2: RSR_ADDRESS[ChainId.Base],
  },
  eusd: {
    symbol: 'eUSD',
    logo: '/svgs/eusd.svg',
    l1: EUSD_ADDRESS[ChainId.Mainnet],
    l2: EUSD_ADDRESS[ChainId.Base],
  },
  usd3: {
    symbol: 'USD3',
    logo: '/svgs/usd3.svg',
    l1: USD3_ADDRESS[ChainId.Mainnet],
    l2: USD3_ADDRESS[ChainId.Base],
  },
  rgusd: {
    symbol: 'rgUSD',
    logo: '/svgs/rgusd.svg',
    l1: RGUSD_ADDRESS[ChainId.Mainnet],
    l2: RGUSD_ADDRESS[ChainId.Base],
  },
}

const Hero = () => (
  <div className="text-center max-w-4xl">
    <h1 className="font-semibold text-4xl sm:text-5xl lg:text-6xl mb-6">
      <Trans>Bridge DTFs and RSR</Trans>
    </h1>
    <p className="md:text-base lg:text-xl">
      <Trans>
        Transfer your DTFs and RSR across Ethereum, Base, and Binance Smart
        Chain. Bridging lets you move tokens between networks so you can trade,
        stake, or use them wherever you need. Select a trusted provider below to
        get started.
      </Trans>
    </p>
  </div>
)
const Rectangle = () => (
  <svg
    width="20"
    height="2"
    viewBox="0 0 20 2"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="1" cy="1" r="1" fill="black" />
    <line x1="4" y1="1" x2="16" y2="1" stroke="black" strokeWidth="1.5" />
    <circle cx="19" cy="1" r="1" fill="black" />
  </svg>
)

const BridgeCard = ({
  name,
  logo,
  url,
  listedTokens,
  shadow,
  targetChain = ChainId.Base
}: Bridge & { shadow?: boolean }) => (
  <div
    className="flex md:grid grid-cols-[1fr_auto_1fr] items-center bg-card p-6 gap-3 rounded-3xl"
    style={
      shadow ? { boxShadow: '0 6px 34px 0 rgba(0, 0, 0, 0.05)' } : undefined
    }
  >
    <div className="flex items-center flex-grow gap-3">
      <img src={logo} alt={name} width={32} height={32} />
      <div className="flex flex-col">
        <h3 className="font-semibold">{name}</h3>
        <a
          className="text-sm text-legend"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url}
        </a>
      </div>
    </div>
    <div className="items-center gap-2 hidden md:flex">
      <ChainLogo chain={ChainId.Mainnet} height={20} width={20} />
      <Rectangle />
      <StackTokenLogo
        tokens={listedTokens.map((token) => ({
          symbol: SUPERBRIDGE_TOKEN_LIST[token].symbol,
          logo: SUPERBRIDGE_TOKEN_LIST[token].logo,
          address: SUPERBRIDGE_TOKEN_LIST[token].l1,
        }))}
      />
      <Rectangle />
      <ChainLogo chain={targetChain} height={20} width={20} />
    </div>
    <div className="flex items-center gap-2 justify-end">
      <span className="text-sm text-legend hidden sm:block">
        <Trans>Visit {name}</Trans>
      </span>
      <a href={url} target="_blank" rel="noopener noreferrer">
        <div className="bg-muted rounded-full p-2">
          <ArrowUpRightIcon className="w-4 h-4" strokeWidth={1.5} />
        </div>
      </a>
    </div>
  </div>
)

const Disclaimer = () => {
  return (
    <div className="flex flex-col gap-4 items-center w-full md:w-[44rem] text-center">
      <div className="border border-foreground rounded-full p-2">
        <OctagonAlert size={16} strokeWidth={1.5} />
      </div>
      <p>
        <Trans>
          Bridging involves external providers. Reserve does not control these
          services and cannot guarantee their security or availability. Use at
          your own discretion.
        </Trans>
      </p>
    </div>
  )
}

const getBridgeUrl = (chain: number, token: { symbol: string, l1: string, l2: string }) => {
  if (chain === ChainId.BSC) {
    return `https://portalbridge.com/?fromChain=Ethereum&fromToken=${token.symbol}&toChain=Bsc&toToken=${token.symbol}`
  }

  return `https://superbridge.app/?fromChainId=1&toChainId=8453&tokenAddress=${token.l1}`
}

const TokenListHelper = ({ tokens, targetChain = ChainId.Base, fromChain = ChainId.Ethereum }: { tokens: BridgeTokenList, targetChain?: number, fromChain?: number }) => (
  <Collapsible>
    <CollapsibleContent>
      {Object.values(tokens).map((token) => (
        <div
          key={token.symbol}
          className={cn(
            'grid grid-cols-2 sm:grid-cols-4 gap-2 p-6',
            'border-b'
          )}
        >
          <div className="flex items-center gap-2">
            <TokenLogo src={token.logo} size="xl" />
            <span className="font-semibold">{token.symbol}</span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <ChainLogo chain={ChainId.Mainnet} height={20} width={20} />
            <span className="text-sm text-legend">
              {shortenAddress(token.l1)}
            </span>
            <CopyValue value={token.l1} />
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <ChainLogo chain={targetChain} height={20} width={20} />
            <span className="text-sm text-legend">
              {shortenAddress(token.l2)}
            </span>
            <CopyValue value={token.l2} />
          </div>
          <a
            href={getBridgeUrl(targetChain, token)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 justify-end text-legend hover:text-primary"
          >
            <span className="text-sm">
              <Trans>Bridge {token.symbol}</Trans>
            </span>

            <Button variant="muted" size="icon-rounded">
              <ArrowUpRight className="w-4 h-4" />
            </Button>
          </a>
        </div>
      ))}
    </CollapsibleContent>
    <CollapsibleTrigger className="p-4 pl-0 sm:pl-8 pr-6 flex  items-center gap-2 text-sm w-full">
      <Info size={16} strokeWidth={1.5} className="hidden sm:block" />
      <div className="ml-3 mr-auto">
        <span className="font-semibold">
          <Trans>Don’t see your token?</Trans>
        </span>{' '}
        <span className="text-legend">
          <Trans>You may need to import it with the L2 contract address.</Trans>
        </span>
      </div>
      <div className="flex items-center text-primary gap-1">
        <span className="hidden md:block">
          <Trans>View list</Trans>
        </span>
        <ChevronsUpDown size={16} strokeWidth={1.5} />
      </div>
    </CollapsibleTrigger>
  </Collapsible>
)

const BridgeList = () => {
  return (
    <div data-testid="bridge-page" className="flex flex-col w-full md:w-[44rem] gap-4 my-10">
      <div className="bg-background rounded-3xl">
        <BridgeCard
          name="Portal"
          logo={portalLogo}
          url="https://portalbridge.com/"
          listedTokens={['rsr']}
          targetChain={ChainId.BSC}
          shadow
        />
        <TokenListHelper tokens={PORTAL_TOKEN_LIST} targetChain={ChainId.BSC} />
      </div>
      <div className="bg-background rounded-3xl">
        <BridgeCard
          name="Superbridge"
          logo={SuperbridgeLogo}
          url="https://superbridge.app/"
          listedTokens={Object.keys(SUPERBRIDGE_TOKEN_LIST)}
          shadow
        />
        <TokenListHelper tokens={SUPERBRIDGE_TOKEN_LIST} />
      </div>
      <BridgeCard
        name="Brid.gg"
        logo={bridgeGGLogo}
        url="https://brid.gg/"
        listedTokens={['rsr']}
      />
    </div>
  )
}

const ChainBridge = () => (
  <div className="container relative h-full">
    <div className="flex flex-col py-16 px-3 sm:px-6  items-center justify-start sm:justify-center gap-2 bg-secondary min-h-full xl:min-h-[calc(100%-24px)] rounded-4xl ">
      <Hero />
      <BridgeList />
      <Disclaimer />
    </div>
  </div>
)

export default ChainBridge
